// file-share-core.js
// Platform-agnostic chunking/reassembly/offer logic — split out from
// file-share.js the same way identity-core.js and group-trust-core.js
// were, so it's bundleable for a browser target. Node's file-share.js
// and src/browser/file-share-browser.js both wrap this with their own
// hashing and I/O (node:crypto + node:fs vs. Web Crypto + File API /
// downloads) — the message format and reassembly logic itself is never
// duplicated across platforms.
//
// Wire format unchanged from file-share.js's own comment: a file-offer
// message (metadata + whole-file hash) followed by N file-chunk
// messages, JSON-tagged the same way settings.js/approval.js are. See
// file-share.js for the full design rationale (why the hash isn't a
// security mechanism, the in-memory scope/limitation, etc.) — this file
// is just the platform-independent half of that same design.

const TYPE_OFFER = 'file-offer/v1'
const TYPE_CHUNK = 'file-chunk/v1'
export const DEFAULT_CHUNK_SIZE = 256 * 1024

let idCounter = 0
const nextFileId = () => `file-${Date.now()}-${idCounter++}`

export function formatFileOffer ({ fileId, filename, size, hash, chunkSize, chunkCount, mimeType }) {
  return JSON.stringify({ type: TYPE_OFFER, fileId, filename, size, hash, chunkSize, chunkCount, mimeType: mimeType ?? null })
}

export function formatFileChunk (fileId, index, base64Data) {
  return JSON.stringify({ type: TYPE_CHUNK, fileId, index, data: base64Data })
}

function parseTagged (text) {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/**
 * @typedef {object} PlatformAdapter
 * @property {(bytes: Uint8Array) => Promise<string>} hashHex - SHA-256, hex-encoded
 * @property {(bytes: Uint8Array) => string} toBase64
 * @property {(base64: string) => Uint8Array} fromBase64
 * @property {(filename: string, bytes: Uint8Array) => Promise<{path?: string}>} writeOutput -
 *   Node: writes to downloadDir and returns {path}. Browser: triggers a
 *   download (or hands back the bytes) and returns {} or a blob URL.
 */

export class FileShareManagerCore {
  /**
   * @param {(messageText: string) => void | Promise<void>} sendFn
   * @param {(result: object) => void} onFileReceived
   * @param {PlatformAdapter} adapter
   * @param {{chunkSize?: number}} [opts]
   */
  constructor (sendFn, onFileReceived, adapter, opts = {}) {
    this.sendFn = sendFn
    this.onFileReceived = onFileReceived
    this.adapter = adapter
    this.chunkSize = opts.chunkSize ?? DEFAULT_CHUNK_SIZE
    this.incoming = new Map()
  }

  /** @param {string} filename @param {Uint8Array} data */
  async offerBytes (filename, data) {
    const hash = await this.adapter.hashHex(data)
    const chunkCount = Math.max(1, Math.ceil(data.length / this.chunkSize))
    const fileId = nextFileId()

    await this.sendFn(formatFileOffer({ fileId, filename, size: data.length, hash, chunkSize: this.chunkSize, chunkCount }))
    for (let i = 0; i < chunkCount; i++) {
      const start = i * this.chunkSize
      const chunk = data.subarray(start, start + this.chunkSize)
      await this.sendFn(formatFileChunk(fileId, i, this.adapter.toBase64(chunk)))
    }
    return { fileId, filename, size: data.length, hash, chunkCount }
  }

  async handleIncomingMessage (messageText, senderIdentifier) {
    const msg = parseTagged(messageText)
    if (!msg) return false

    if (msg.type === TYPE_OFFER) {
      this.incoming.set(msg.fileId, {
        filename: msg.filename, size: msg.size, hash: msg.hash,
        chunkCount: msg.chunkCount, mimeType: msg.mimeType,
        sender: senderIdentifier, chunks: new Map()
      })
      return true
    }

    if (msg.type === TYPE_CHUNK) {
      const transfer = this.incoming.get(msg.fileId)
      if (!transfer) return true

      transfer.chunks.set(msg.index, this.adapter.fromBase64(msg.data))
      if (transfer.chunks.size === transfer.chunkCount) {
        this.incoming.delete(msg.fileId)
        await this._completeTransfer(msg.fileId, transfer)
      }
      return true
    }

    return false
  }

  async _completeTransfer (fileId, transfer) {
    const parts = []
    let total = 0
    for (let i = 0; i < transfer.chunkCount; i++) {
      const chunk = transfer.chunks.get(i)
      if (!chunk) {
        this.onFileReceived?.({ fileId, filename: transfer.filename, error: `missing chunk ${i} of ${transfer.chunkCount}` })
        return
      }
      parts.push(chunk)
      total += chunk.length
    }
    const full = new Uint8Array(total)
    let offset = 0
    for (const part of parts) { full.set(part, offset); offset += part.length }

    const actualHash = await this.adapter.hashHex(full)
    if (actualHash !== transfer.hash) {
      this.onFileReceived?.({ fileId, filename: transfer.filename, error: `hash mismatch: expected ${transfer.hash}, got ${actualHash}` })
      return
    }

    const output = await this.adapter.writeOutput(transfer.filename, full)
    this.onFileReceived?.({ fileId, filename: transfer.filename, size: full.length, sender: transfer.sender, ...output })
  }
}
